import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getDomainId } from 'api-client/helpers'
import type { Tenant } from 'api-client/keystone.model'
import type { RootState } from 'app/store'
import useListAction from 'core/hooks/useListAction'
import { sessionStoreKey } from 'core/session/sessionReducers'
import { canManageAllDomains } from 'core/utils/rbac'
import { listAllDomainTenants, listUserTenants } from './new-actions'
import { allDomainTenantsSelector, userTenantsSelector } from './selectors'

const useTenantSelector = (selectedDomain: string = getDomainId()) => {
  const [data, setData] = useState<Array<Tenant>>([])
  // Subscribe to role changes so the hook re-evaluates when the session resets
  useSelector((state: RootState) => state[sessionStoreKey]?.userDetails?.role)
  const canManageAllDomainsUser = canManageAllDomains()

  // Fetch all tenants
  const { loading: loadingAllDomainTenants, reload: reloadAllDomainTenants } = useListAction(
    listAllDomainTenants,
    {
      preRequisites: canManageAllDomainsUser,
    },
  )
  const { loading: loadingUserTenant, reload: reloadUserTenants } = useListAction(listUserTenants, {
    preRequisites: !canManageAllDomainsUser,
  })
  const allDomainTenants = useSelector(allDomainTenantsSelector)
  const userTenants = useSelector(userTenantsSelector)

  // Based on user role, use the states from different selectors
  const allTenants = canManageAllDomainsUser ? allDomainTenants : userTenants
  const loading = canManageAllDomainsUser ? loadingAllDomainTenants : loadingUserTenant
  const reload = canManageAllDomainsUser ? reloadAllDomainTenants : reloadUserTenants

  // Filter tenants based on selected domain
  useEffect(() => {
    if (selectedDomain && !loading) {
      setData(allTenants?.filter((tenant) => tenant.domain_id === selectedDomain))
    }
  }, [selectedDomain, allTenants, loading])

  return {
    loading,
    reload,
    data,
  }
}

export default useTenantSelector
